import { useEffect, useRef } from 'react';

function AnimatedBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animId;
    let w, h;
    let traces = [];
    let nodes = [];
    let signals = [];
    let aiOutlinePoints = [];

    const GRID = 40;
    const GREEN = [0, 255, 157];
    const CYAN = [0, 200, 255];
    const PURPLE = [167, 139, 250];

    function resize() {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
      generateCircuitry();
      extractAIOutline();
    }

    function snap(v) {
      return Math.round(v / GRID) * GRID;
    }

    function extractAIOutline() {
      aiOutlinePoints = [];
      const offscreen = document.createElement('canvas');
      const fontSize = Math.min(w * 0.45, h * 0.55, 500);
      const pad = 40;
      offscreen.width = w;
      offscreen.height = h;
      const offCtx = offscreen.getContext('2d');

      offCtx.clearRect(0, 0, w, h);
      offCtx.save();
      offCtx.translate(w / 2, h / 2);
      offCtx.rotate(-15 * Math.PI / 180);
      offCtx.font = `900 ${fontSize}px Inter, Arial, sans-serif`;
      offCtx.textAlign = 'center';
      offCtx.textBaseline = 'middle';
      offCtx.strokeStyle = '#fff';
      offCtx.lineWidth = 2;
      offCtx.strokeText('AI', 0, 0);
      offCtx.restore();

      const imageData = offCtx.getImageData(0, 0, w, h);
      const data = imageData.data;
      const step = 3;

      for (let y = 0; y < h; y += step) {
        for (let x = 0; x < w; x += step) {
          const idx = (y * w + x) * 4;
          if (data[idx + 3] > 100) {
            aiOutlinePoints.push({ x, y });
          }
        }
      }
    }

    function generateCircuitry() {
      traces = [];
      nodes = [];
      signals = [];

      const cols = Math.ceil(w / GRID);
      const rows = Math.ceil(h / GRID);
      const nodeCount = Math.floor((cols * rows) * 0.04);

      for (let i = 0; i < nodeCount; i++) {
        const nx = snap(Math.random() * w);
        const ny = snap(Math.random() * h);
        const type = Math.random();
        nodes.push({
          x: nx,
          y: ny,
          size: type < 0.3 ? 4 : type < 0.7 ? 3 : 2,
          shape: type < 0.2 ? 'square' : type < 0.5 ? 'diamond' : 'circle',
          pulse: Math.random() * Math.PI * 2,
          color: type < 0.6 ? GREEN : type < 0.85 ? CYAN : PURPLE,
        });
      }

      for (let i = 0; i < nodes.length; i++) {
        const from = nodes[i];
        let nearest = [];
        for (let j = 0; j < nodes.length; j++) {
          if (i === j) continue;
          const dist = Math.abs(from.x - nodes[j].x) + Math.abs(from.y - nodes[j].y);
          if (dist < GRID * 12 && dist > GRID) {
            nearest.push({ idx: j, dist });
          }
        }
        nearest.sort((a, b) => a.dist - b.dist);
        const connectCount = Math.min(nearest.length, 1 + Math.floor(Math.random() * 2));

        for (let c = 0; c < connectCount; c++) {
          const to = nodes[nearest[c].idx];
          const path = [];
          path.push({ x: from.x, y: from.y });

          const midStyle = Math.random();
          if (midStyle < 0.4) {
            path.push({ x: to.x, y: from.y });
            path.push({ x: to.x, y: to.y });
          } else if (midStyle < 0.8) {
            path.push({ x: from.x, y: to.y });
            path.push({ x: to.x, y: to.y });
          } else {
            const midX = snap((from.x + to.x) / 2);
            path.push({ x: midX, y: from.y });
            path.push({ x: midX, y: to.y });
            path.push({ x: to.x, y: to.y });
          }

          const color = from.color;
          traces.push({ path, color, opacity: 0.06 + Math.random() * 0.06 });
        }
      }

      const signalCount = Math.floor(traces.length * 0.35);
      for (let i = 0; i < signalCount; i++) {
        const traceIdx = Math.floor(Math.random() * traces.length);
        signals.push({
          traceIdx,
          progress: Math.random(),
          speed: 0.002 + Math.random() * 0.004,
          length: 0.08 + Math.random() * 0.12,
          color: traces[traceIdx].color,
        });
      }
    }

    function getPointOnTrace(trace, t) {
      const path = trace.path;
      const segments = [];
      let totalLen = 0;
      for (let i = 1; i < path.length; i++) {
        const dx = path[i].x - path[i - 1].x;
        const dy = path[i].y - path[i - 1].y;
        const len = Math.sqrt(dx * dx + dy * dy);
        segments.push({ from: path[i - 1], to: path[i], len });
        totalLen += len;
      }
      if (totalLen === 0) return path[0];
      let target = t * totalLen;
      for (const seg of segments) {
        if (target <= seg.len) {
          const frac = seg.len > 0 ? target / seg.len : 0;
          return {
            x: seg.from.x + (seg.to.x - seg.from.x) * frac,
            y: seg.from.y + (seg.to.y - seg.from.y) * frac,
          };
        }
        target -= seg.len;
      }
      return path[path.length - 1];
    }

    resize();
    window.addEventListener('resize', resize);

    let t = 0;

    function draw() {
      ctx.clearRect(0, 0, w, h);
      t += 0.006;

      ctx.save();
      ctx.translate(w / 2, h / 2);
      ctx.rotate(-15 * Math.PI / 180);
      const fontSize = Math.min(w * 0.45, h * 0.55, 500);
      ctx.font = `900 ${fontSize}px Inter, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = 'rgba(0, 255, 157, 0.03)';
      ctx.fillText('AI', 0, 0);
      ctx.restore();

      if (aiOutlinePoints.length > 0) {
        const traceWindow = 0.06;
        const tracerSpeed = 0.15;
        const tracerPos = (t * tracerSpeed) % 1;

        for (let i = 0; i < aiOutlinePoints.length; i++) {
          const frac = i / aiOutlinePoints.length;
          let dist = Math.abs(frac - tracerPos);
          if (dist > 0.5) dist = 1 - dist;

          if (dist < traceWindow) {
            const intensity = 1 - (dist / traceWindow);
            const pt = aiOutlinePoints[i];

            if (intensity > 0.7) {
              ctx.beginPath();
              ctx.arc(pt.x, pt.y, 6, 0, Math.PI * 2);
              ctx.fillStyle = `rgba(0,255,157,${intensity * 0.06})`;
              ctx.fill();
            }

            ctx.beginPath();
            ctx.arc(pt.x, pt.y, 1.2, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(0,255,157,${intensity * 0.7})`;
            ctx.fill();
          } else {
            const pt = aiOutlinePoints[i];
            ctx.beginPath();
            ctx.arc(pt.x, pt.y, 0.6, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(0,255,157,0.04)';
            ctx.fill();
          }
        }
      }

      ctx.strokeStyle = `rgba(${GREEN[0]},${GREEN[1]},${GREEN[2]},0.02)`;
      ctx.lineWidth = 0.5;
      for (let x = 0; x <= w; x += GRID) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h);
        ctx.stroke();
      }
      for (let y = 0; y <= h; y += GRID) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(w, y);
        ctx.stroke();
      }

      traces.forEach(trace => {
        ctx.beginPath();
        trace.path.forEach((p, i) => {
          if (i === 0) ctx.moveTo(p.x, p.y);
          else ctx.lineTo(p.x, p.y);
        });
        ctx.strokeStyle = `rgba(${trace.color[0]},${trace.color[1]},${trace.color[2]},${trace.opacity})`;
        ctx.lineWidth = 1.2;
        ctx.stroke();
      });

      signals.forEach(sig => {
        sig.progress += sig.speed;
        if (sig.progress > 1 + sig.length) sig.progress = -sig.length;

        const trace = traces[sig.traceIdx];
        const steps = 8;
        for (let s = 0; s < steps; s++) {
          const st = sig.progress - (s / steps) * sig.length;
          if (st < 0 || st > 1) continue;
          const pt = getPointOnTrace(trace, st);
          const alpha = (1 - s / steps) * 0.6;
          const radius = (1 - s / steps) * 2.5;

          ctx.beginPath();
          ctx.arc(pt.x, pt.y, radius + 3, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${sig.color[0]},${sig.color[1]},${sig.color[2]},${alpha * 0.1})`;
          ctx.fill();

          ctx.beginPath();
          ctx.arc(pt.x, pt.y, radius, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${sig.color[0]},${sig.color[1]},${sig.color[2]},${alpha})`;
          ctx.fill();
        }
      });

      nodes.forEach(node => {
        node.pulse += 0.015;
        const glow = 0.25 + Math.sin(node.pulse) * 0.15;
        const col = node.color;

        ctx.beginPath();
        ctx.arc(node.x, node.y, node.size + 6, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${col[0]},${col[1]},${col[2]},${glow * 0.08})`;
        ctx.fill();

        if (node.shape === 'square') {
          const s = node.size;
          ctx.fillStyle = `rgba(${col[0]},${col[1]},${col[2]},${glow})`;
          ctx.fillRect(node.x - s, node.y - s, s * 2, s * 2);
          ctx.strokeStyle = `rgba(${col[0]},${col[1]},${col[2]},${glow * 0.5})`;
          ctx.lineWidth = 0.8;
          ctx.strokeRect(node.x - s - 2, node.y - s - 2, s * 2 + 4, s * 2 + 4);
        } else if (node.shape === 'diamond') {
          const s = node.size;
          ctx.beginPath();
          ctx.moveTo(node.x, node.y - s);
          ctx.lineTo(node.x + s, node.y);
          ctx.lineTo(node.x, node.y + s);
          ctx.lineTo(node.x - s, node.y);
          ctx.closePath();
          ctx.fillStyle = `rgba(${col[0]},${col[1]},${col[2]},${glow})`;
          ctx.fill();
        } else {
          ctx.beginPath();
          ctx.arc(node.x, node.y, node.size, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(${col[0]},${col[1]},${col[2]},${glow})`;
          ctx.fill();

          ctx.beginPath();
          ctx.arc(node.x, node.y, node.size + 2, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(${col[0]},${col[1]},${col[2]},${glow * 0.3})`;
          ctx.lineWidth = 0.6;
          ctx.stroke();
        }
      });

      animId = requestAnimationFrame(draw);
    }

    draw();

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        pointerEvents: 'none',
      }}
    />
  );
}

export default AnimatedBackground;
